import { inject, Injectable } from '@angular/core';
import { Observable, of, OperatorFunction, pipe, Subject, UnaryFunction } from 'rxjs';
import { routeNoProjectsAvailable, routeProjects } from '../liveformsweb.routes';
import { AppUrls } from '../route/app-urls';
import { HttpClient } from '@angular/common/http';
import { catchError, concatMap, defaultIfEmpty, filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { LFProject, LFProjectFactory, ProjectDTO } from '@lfw/roesberg.liveforms.core';
import { ErrorHandlers } from '../shared/helpers/error-handlers';
import { AuthConfig } from 'angular-oauth2-oidc';
import { ActivatedRoute } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class ProjectsService {
  private selectedProject: LFProject | undefined;

  private projectCache$!: Observable<LFProject[]>;
  private projectLoadError = '';

  get projectSelectErrorCode() {
    return this.projectLoadError.split(':')[0];
  }

  get allProjects$(): Observable<LFProject[]> {
    this.projectLoadError = '';
    if (!this.projectCache$) {
      this.projectCache$ = this.getAllProjects().pipe(shareReplay(1));
    }
    return this.projectCache$;
  }

  public projectChange: Subject<boolean> = new Subject<boolean>();
  public navigate: Subject<string> = new Subject<string>();

  private http = inject(HttpClient);
  private route = inject(ActivatedRoute);
  private authConfig = inject(AuthConfig);

  public get isProjectSelected(): boolean {
    return this.selectedProject != undefined;
  }

  public get projectsAvailable$(): Observable<boolean> {
    return this.allProjects$.pipe(map((res) => res.length > 0));
  }

  public get moreThanOneProjectAvailable$(): Observable<boolean> {
    return this.allProjects$.pipe(map((res) => res.length > 1));
  }

  public getProjectByName$(name: string): Observable<string> {
    name = name.toLowerCase();
    return this.allProjects$.pipe(
      //tap((wd) => console.log(`all`, wd)),
      map((wds) => wds.find((info) => info.text.toLowerCase() === name)),
      //tap((wds) => console.log(`result `, wds)),
      this.filterNullish(),
      switchMap((item) => of(item.id)),
      defaultIfEmpty('')
    );
  }

  public get currentProjectId() {
    if (this.selectedProject == undefined) {
      return '';
    }
    return this.selectedProject.id;
  }

  public get currentProjectName() {
    if (this.selectedProject == undefined) {
      return '';
    }
    return this.selectedProject.text;
  }

  public get currentProjectUserCanCancelOrder() {
    if (this.selectedProject == undefined || this.selectedProject.rights == undefined) {
      return false;
    }
    return this.selectedProject.rights.cancelOrders;
  }

  private getAllProjects(): Observable<LFProject[]> {
    return this.http.get<LFProject[]>(AppUrls.getApiUrl(AppUrls.urlProjects, 'getall')).pipe(
      catchError((error) => {
        return ErrorHandlers.errorHandler(error);
      })
    );
  }
  /*
  public getDetails(): Observable<LFProjectNodeValue[]> {
    return this.http
      .get<LFProjectNodeValue[]>(
        AppUrls.getApiUrl(AppUrls.urlProject, 'getdetails') + '?projectId=' + this.currentProjectId
      )
      .pipe(
        catchError((error) => {
          return ErrorHandlers.errorHandler(error);
        })
      );
  }
*/

  filterNullish<T>(): UnaryFunction<Observable<T | null | undefined>, Observable<T>> {
    return pipe(filter((x) => x != null) as OperatorFunction<T | null | undefined, T>);
  }

  public selectProject(id: string): Observable<boolean> {
    this.projectLoadError = '';
    if (this.selectedProject != undefined && this.selectedProject.id == id) {
      return of(true);
    }
    return this.allProjects$.pipe(
      // tap((wd) => console.log(`try to find wd ${wd}`)),
      map((wds) => wds.find((info) => info.id === id)),
      this.filterNullish(),
      switchMap((item) => this.selectProjectItem(item)),
      defaultIfEmpty(false)
    );
  }

  private selectProjectItem(project: LFProject): Observable<boolean> {
    this.projectLoadError = '';
    return this.http
      .get<ProjectDTO>(AppUrls.getApiUrl(AppUrls.urlProjects, 'getproject') + '?projectId=' + project.id)
      .pipe(
        map((dtoRaw) => LFProjectFactory.fromDTO(dtoRaw)),
        map((res) => {
          this.selectedProject = res;
          this.projectChange.next(true);
          return true;
        }),
        catchError((error) => {
          console.error(`select project failed`, error, project.id);
          this.projectLoadError = error.error.message;
          return of(false);
          // return ErrorHandlers.errorHandler(error);
        })
      );
  }

  retrieveUserState(state: string) {
    let userState = '';
    if (state) {
      const sep = this.authConfig.nonceStateSeparator || ';';
      const idx = state.indexOf(sep);
      if (idx > -1) {
        userState = state.substr(idx + sep.length);
      }
    }
    return userState;
  }

  public initializeProjects() {
    const snapshot = this.route.snapshot;
    const state = snapshot.root.queryParams['state'];
    let userState = '';
    if (state) {
      userState = this.retrieveUserState(decodeURIComponent(state));
    }

    // "load" all records
    this.getAllProjects()
      .pipe(
        concatMap(() => this.initOnSuccess(userState)),
        take(1)
      )
      .subscribe();
  }

  // check, if state contains a project (that is also available)
  // if not,  "no project available"
  //
  private initOnSuccess(userState: string): Observable<boolean> {
    return this.projectsAvailable$.pipe(
      map((projectAvailable) => {
        if (!projectAvailable) {
          this.navigate.next(`/${routeNoProjectsAvailable}`);
          return true;
        } else {
          if (userState) {
            this.navigate.next(userState);
            return true;
          }
        }
        return false;
      }),
      filter((res) => !res),
      switchMap(() => this.moreThanOneProjectAvailable$),
      map(
        (moreThanOneWebDokuAvailable) => {
          if (moreThanOneWebDokuAvailable) {
            this.navigate.next(`/${routeProjects}`);
            return true;
          }
          return false;
        },
        filter((res) => !res)
        // todo MN
        //switchMap(() => this.selectWebDokuItem()) m(())
      )
    );
  }
}
